import { CreditCard, Loader2 } from "lucide-react";
import { Controller, useWatch, type UseFormReturn } from "react-hook-form";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { Translations } from "@/lib/i18n/types";
import {
  isAutomaticPaymentMethod,
  type BillingContext,
  type BillingDetailsInput,
  type BillingPaymentMethod,
} from "../types";
import { RestrictedActionButton } from "./RestrictedActionButton";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

/** Invoice-transfer plumbing remains available internally, but is not offered to customers. */
const PAYMENT_METHODS: readonly BillingPaymentMethod[] = ["card", "sepa_debit"];

function useMethodLabels() {
  const copy = useLanguage().t.billing.details;
  const byMethod: Record<BillingPaymentMethod, string> = {
    card: copy.paymentByCard,
    sepa_debit: copy.paymentBySepa,
    bank_transfer_email: copy.paymentByEmail,
    bank_transfer_e_invoice: copy.paymentByEInvoice,
  };
  return byMethod;
}

/**
 * What the on-file row says for the PICKED method.
 *
 * The attached method only counts when it IS the picked one: an organisation
 * that saved a card and is now looking at SEPA must not read "Visa •••• 4242"
 * under the SEPA hint, because switching does not carry the card over.
 *
 */
function onFileState(
  context: BillingContext,
  picked: BillingPaymentMethod,
  copy: Translations["billing"]["details"]
) {
  const onFile = context.payment_method_on_file;
  const attached = onFile?.type === picked ? onFile : null;
  return {
    label: attached ? attached.label : copy.noPaymentMethod,
    action: attached ? copy.changePaymentMethod : copy.setUpPaymentMethod,
  };
}

/**
 * What Stripe holds on file for the method that is currently picked, and the
 * way to change it.
 *
 * Only rendered for the automatic methods: a bank transfer needs nothing
 * attached, and offering Checkout there would collect a card the subscription
 * would never charge.
 *
 * Both the label and the button are keyed off the PICKED value rather than the
 * attached one — see `onFileState` for why.
 */
function PaymentMethodOnFile({
  context,
  picked,
  restriction,
  onSetup,
  disabled,
}: {
  context: BillingContext;
  picked: BillingPaymentMethod;
  restriction?: string;
  onSetup: () => Promise<void>;
  disabled: boolean;
}) {
  const copy = useLanguage().t.billing.details;
  const { label, action } = onFileState(context, picked, copy);

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-border px-4 py-3">
      <CreditCard className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
      <p className="text-sm font-medium">{label}</p>
      <RestrictedActionButton
        type="button"
        variant="outline"
        size="sm"
        className="ml-auto"
        disabledReason={restriction}
        disabled={disabled}
        onClick={onSetup}
      >
        {disabled && !restriction && <Loader2 className="h-4 w-4 animate-spin" />}
        {action}
      </RestrictedActionButton>
    </div>
  );
}

export function PaymentMethodField({
  form,
  disabled,
  restriction,
  context,
  onSetup,
}: {
  form: UseFormReturn<BillingDetailsInput>;
  disabled: boolean;
  /** Why the form is read-only, or undefined when it is editable. */
  restriction?: string;
  context: BillingContext;
  onSetup: () => Promise<void>;
}) {
  const copy = useLanguage().t.billing.details;
  const labels = useMethodLabels();
  // The picked value, not the saved one: choosing Card must reveal the setup
  // affordance immediately, otherwise the customer has to save, reload and
  // find it — three steps for one decision.
  const picked = useWatch({ control: form.control, name: "paymentMethod" });
  const automatic = isAutomaticPaymentMethod(picked);

  return (
    <div className="space-y-2 sm:col-span-2">
      <Label htmlFor="billing-payment-method">{copy.paymentMethod}</Label>
      <Controller
        control={form.control}
        name="paymentMethod"
        render={({ field }) => (
          <Select value={field.value} onValueChange={field.onChange} disabled={disabled}>
            <SelectTrigger id="billing-payment-method" {...pageInfoTarget("billing.paymentMethod")}>
              <SelectValue>{automatic ? undefined : labels[field.value]}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {PAYMENT_METHODS.map((method) => (
                <SelectItem key={method} value={method}>
                  {method === "card" ? `${labels[method]} ${copy.recommended}` : labels[method]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      />
      <p className="text-sm text-muted-foreground">
        {automatic ? copy.paymentHintAutomatic : copy.paymentHint}
      </p>
      {automatic && (
        <PaymentMethodOnFile
          context={context}
          picked={picked}
          restriction={restriction}
          disabled={disabled}
          onSetup={onSetup}
        />
      )}
    </div>
  );
}
